import { useState } from "react"
import { Steps, message } from "@mimber/ui-web"
import { useCreateImportJob } from "@mimber/client/mimber-organization"
import { UploadStep } from "./steps/UploadStep"
import { ColumnMappingStep } from "./steps/ColumnMappingStep"
import { ReviewStep } from "./steps/ReviewStep"
import type {
  ColumnMapping,
  ImportWizardModalProps,
  ParsedCsvPreview,
} from "./ImportWizardModal.types"

const STEP_TITLES = ["Upload", "Map columns", "Review"]

/**
 * Generic, config-driven CSV import wizard. Only knows how to upload a file,
 * map its columns, and submit — all business logic (target fields, template,
 * permission) comes from `config`, so this same component can back future
 * import types (events, invoices) without changes here.
 */
export const ImportWizardModal: React.FunctionComponent<
  ImportWizardModalProps
> = ({ config, onSuccess }) => {
  const [stepIndex, setStepIndex] = useState(0)
  const [csvPreview, setCsvPreview] = useState<ParsedCsvPreview | null>(null)
  const [mapping, setMapping] = useState<ColumnMapping>({})

  const createImportJob = useCreateImportJob()

  const handleParsed = (result: ParsedCsvPreview) => {
    setCsvPreview(result)
    setMapping(autoMapColumns(result.headers, config.fieldDefinitions))
    setStepIndex(1)
  }

  const handleConfirm = () => {
    if (!csvPreview) return

    createImportJob.mutate(
      {
        file: csvPreview.file,
        type: config.importType,
        columnMapping: mapping,
      },
      {
        onSuccess: () => {
          message.success(config.successMessage)
          onSuccess?.()
        },
        onError: (error) => {
          message.error(error.message || "Failed to start the import")
        },
      }
    )
  }

  return (
    <div>
      <Steps
        current={stepIndex}
        size="small"
        items={STEP_TITLES.map((title) => ({ title }))}
        style={{ marginBottom: 24 }}
      />

      {stepIndex === 0 && (
        <UploadStep
          templateHeaders={config.templateHeaders}
          templateFilename={config.templateFilename}
          onParsed={handleParsed}
        />
      )}

      {stepIndex === 1 && csvPreview && (
        <ColumnMappingStep
          csvHeaders={csvPreview.headers}
          fieldDefinitions={config.fieldDefinitions}
          mapping={mapping}
          onChange={setMapping}
          onBack={() => setStepIndex(0)}
          onNext={() => setStepIndex(2)}
        />
      )}

      {stepIndex === 2 && csvPreview && (
        <ReviewStep
          file={csvPreview.file}
          mapping={mapping}
          fieldDefinitions={config.fieldDefinitions}
          isPending={createImportJob.isPending}
          onBack={() => setStepIndex(1)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  )
}

/** Best-effort default mapping — matches CSV headers to field keys/labels case-insensitively. */
function autoMapColumns(
  headers: string[],
  fieldDefinitions: ImportWizardModalProps["config"]["fieldDefinitions"]
): ColumnMapping {
  const mapping: ColumnMapping = {}
  const usedFieldKeys = new Set<string>()

  for (const header of headers) {
    const normalized = header
      .trim()
      .toLowerCase()
      .replace(/[\s_-]/g, "")
    const match = fieldDefinitions.find((field) => {
      if (usedFieldKeys.has(field.key)) return false
      const key = field.key.toLowerCase()
      const label = field.label.toLowerCase().replace(/[\s_-]/g, "")
      return normalized === key || normalized === label
    })
    if (match) {
      mapping[header] = match.key
      usedFieldKeys.add(match.key)
    }
  }

  return mapping
}
